import './index.css';
import { View, Text } from '../../../component';
import { useMemo, useState, useRef, useEffect } from 'react';
import React from 'react';
import Constants from '../../../constants';
import YNQuestionStep from '../../component/yn_question_step';
import YNQuestionCard from '../../component/yn_question_card';
import { vs, s } from '../../../utilities/scaling_utils';
import useTheme from '../../../utilities/hooks/theme';
import { isDesktopBrowser } from '../../../utilities/helper';
import InsightInfo from '../../component/insight_info';
import { useDispatch } from 'react-redux';
import { showToast } from '../../../actions/app-actions-types';

interface Props {
  questionList?: any;
  updateQuestionList?: any;
  onPressChangeTab?: any;
  language?: any;
}

const MyAnswerTab = ({
  questionList,
  updateQuestionList,
  onPressChangeTab,
  language = 'en'
}: Props) => {
  const dispatch = useDispatch();
  const { FantasyTheme: theme } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(questionList.length - 1);
  const [currentStep, setCurrentStep] = useState(1);
  const [insightInfoModal, setInsightInfoModal] = useState(false);
  const currentIndexRef = useRef(currentIndex);
  const [insight, setInsight] = useState('');

  useEffect(() => {
    setCurrentIndex(questionList.length - 1);
    currentIndexRef.current = questionList.length - 1;
    window.addEventListener('message', eventListener);
    return () => {
      window.removeEventListener('message', eventListener);
    };
  }, [questionList.length]);

  useEffect(() => {
    window.addEventListener('popstate', onClickBrowserBack);

    return () => {
      window.removeEventListener('popstate', onClickBrowserBack);
    };
  }, []);

  const onClickBrowserBack = () => {
    setInsightInfoModal(false);
  };

  const eventListener = (event: any) => {
    if (event.data.command_type === 'dismiss_insight_info') {
      onPressClose();
    }
  };

  const canSwipe = currentIndex >= 0;
  const childRefs: any = useMemo(
    () =>
      Array(questionList.length)
        .fill(0)
        .map(() => React.createRef()),
    [questionList]
  );

  const updateCurrentIndex = (val: any) => {
    setCurrentIndex(val);
    currentIndexRef.current = val;
    setCurrentStep(questionList.length - val);
    if (questionList.length - val === 11) onPressChangeTab(1);
  };

  // set last direction and decrease current index
  const onSwipe = (_direction: any, _nameToDelete: any, index: number) => {
    if (_direction === 'right') {
      changeAnswerYes(index);
    } else if (_direction === 'left') {
      changeAnswerNo(index);
    }
    updateCurrentIndex(index - 1);
  };

  const swipe = async (dir: any) => {
    try {
      if (canSwipe && currentIndex < questionList.length) {
        await childRefs[currentIndex].current.swipe(dir); // Swipe the card!
      }
      // eslint-disable-next-line no-empty
    } catch (error) {
      console.log('error ', error);
    }
  };

  const onPressYes = () => {
    swipe('left');
  };

  const changeAnswerYes = (index: number) => {
    questionList[index].answer = true;
    updateQuestionList([...questionList]);
  };

  const onPressNo = () => {
    swipe('right');
  };

  const changeAnswerNo = (index: number) => {
    questionList[index].answer = false;
    updateQuestionList([...questionList]);
  };

  const onPress2x = () => {
    questionList[currentIndex].isPowerUp = !questionList[currentIndex].isPowerUp;
    updateQuestionList([...questionList]);
  };

  const currentStepText = {
    color: theme.myQueAnsCurrentStep
  };

  const totalStepText = {
    color: theme.myQueAnsTotalStep
  };

  const myQueAns2xText = {
    color: theme.myQueAns2xText
  };

  const onPressClose = () => {
    setInsightInfoModal(false);
  };

  const onPressInfo = (event: any) => {
    event.stopPropagation();
    const insightData = questionList[currentIndex]?.insights || {};
    const { english = '' } = insightData;
    if (english) {
      setInsight(insightData);
      setInsightInfoModal(true);
    } else {
      dispatch(showToast('There is no data available'));
    }
  };

  const btnDimension = isDesktopBrowser ? s(70) : s(60);

  return (
    <View>
      <View className="myQuestionList__headerWrapper">
        <Text style={currentStepText} className="myQuestionList__title">{`${currentStep}`}</Text>
        <Text style={totalStepText} className="myQuestionList__value">
          &nbsp;{`/ ${questionList.length}`}
        </Text>
      </View>
      <YNQuestionStep questionList={questionList} currentStep={currentStep} />
      <View className="myQuestionList__container">
        {questionList.map((questionItem: { _id: React.Key | null | undefined }, index: any) => {
          return (
            <YNQuestionCard
              key={questionItem._id}
              tinderCardRef={childRefs[index]}
              index={index}
              questionItem={questionItem}
              onSwipe={onSwipe}
              language={language}
              onPressInfo={onPressInfo}
            />
          );
        })}
        <View
          style={{
            marginTop: vs(360),
            marginLeft: s(20),
            marginRight: s(20)
          }}
          className="myQuestionList__bottomWrapper">
          <View onPress={onPressYes}>
            <Constants.Svgs.YesNo_NoBtn width={btnDimension} height={btnDimension} />
            <Text style={myQueAns2xText} className="myQuestionList__btnTitle">
              {language === 'en' ? 'No' : 'नहीं'}
            </Text>
          </View>
          <View onPress={onPress2x}>
            <Constants.Svgs.YesNo_2xBtn width={btnDimension} height={btnDimension} />
            <Text style={myQueAns2xText} className="myQuestionList__btnTitle">
              {language === 'en' ? 'Double Up' : 'दोगुना'}
            </Text>
          </View>
          <View onPress={onPressNo}>
            <Constants.Svgs.YesNo_YesBtn width={btnDimension} height={btnDimension} />
            <Text style={myQueAns2xText} className="myQuestionList__btnTitle">
              {language === 'en' ? 'Yes' : 'हाँ'}
            </Text>
          </View>
        </View>
      </View>
      <InsightInfo
        visible={insightInfoModal}
        onPressClose={onPressClose}
        insight={insight}
        language={language}
      />
    </View>
  );
};
export default React.memo(MyAnswerTab);
